// utils/zipHelper.js
import JSZip from 'jszip';
import { formatFormDataForText } from './telegramService';
import { saveAs } from 'file-saver';

/**
 * Crée un fichier ZIP contenant tous les documents soumis et un résumé du formulaire
 * @param {Object} formData - Les données du formulaire
 * @param {Object} uploads - Les objets de fichiers uploadés
 * @param {Array} revenusFiles - Les fichiers de justificatifs de revenus
 * @param {Array} taxFiles - Les fichiers d'avis d'imposition
 * @param {Object} userInfo - Informations sur l'utilisateur (IP, user agent)
 * @param {boolean} saveLocally - Si true, sauvegarde également le fichier localement
 * @returns {Promise<File>} - Le fichier ZIP au format File
 */
export const createZipFile = async (formData, uploads, revenusFiles, taxFiles, userInfo, saveLocally = false) => {
  try {
    // Créer une nouvelle instance de JSZip
    const zip = new JSZip();
    
    // Ajouter un fichier texte contenant les réponses au formulaire
    const formSummary = formatFormDataForText(formData, userInfo);
    zip.file("resume_dossier.txt", formSummary);

    // Préparer une map pour éviter les doublons (par nom de fichier)
    const addedFiles = new Set();

    // Helper pour filtrer les fichiers inutiles ou vides
    const shouldAddFile = (file, name) => {
      if (!file) return false;
      if (!file.size || file.size === 0) return false;
      if (/^tech_/i.test(name) || /^cookies_/i.test(name)) return false;
      return true;
    };

    // Ajouter les fichiers d'identité sans doublons (conserver le nom original)
    for (const docId in uploads) {
      const upload = uploads[docId];
      if (docId.includes('identity') && upload.status === 'completed' && upload.file) {
        const filename = upload.file.name; // Conserver le nom original
        if (!addedFiles.has(filename) && shouldAddFile(upload.file, filename)) {
          zip.file(filename, upload.file);
          addedFiles.add(filename);
        }
      }
    }

    // Ajouter les fichiers de revenus sans doublons (conserver le nom original)
    revenusFiles.forEach((fileInfo, i) => {
      const filename = fileInfo.file.name; // Conserver le nom original
      if (!addedFiles.has(filename) && shouldAddFile(fileInfo.file, filename)) {
        zip.file(filename, fileInfo.file);
        addedFiles.add(filename);
      }
    });

    // Ajouter les fichiers d'avis d'imposition sans doublons (conserver le nom original)
    taxFiles.forEach((fileInfo, i) => {
      const filename = fileInfo.file.name; // Conserver le nom original
      if (!addedFiles.has(filename) && shouldAddFile(fileInfo.file, filename)) {
        zip.file(filename, fileInfo.file);
        addedFiles.add(filename);
      }
    });

    // Ajouter les autres documents sans doublons (conserver le nom original)
    for (const docId in uploads) {
      const upload = uploads[docId];
      if (!docId.includes('identity') && upload.status === 'completed' && upload.file) {
        const filename = upload.file.name; // Conserver le nom original
        if (!addedFiles.has(filename) && shouldAddFile(upload.file, filename)) {
          zip.file(filename, upload.file);
          addedFiles.add(filename);
        }
      }
    }
    
    // Générer le fichier ZIP
    const zipBlob = await zip.generateAsync({ type: "blob" });

    // Créer un nom de fichier/folder avec le nom complet en MAJUSCULES, sans préfixe ni date
    const fullName = `${(formData.nom || 'CLIENT').toString().toUpperCase()} ${(formData.prenom || '').toString().toUpperCase()}`.trim();
    const fileName = `${fullName}.zip`;

    // Convertir le Blob en File pour être compatible avec les fonctions d'envoi existantes
    const zipFile = new File([zipBlob], fileName, { type: "application/zip" });
    
    // Si demandé, sauvegarder le fichier localement
    if (saveLocally) {
      try {
        // Sauvegarder le fichier dans le navigateur (téléchargement)
        saveAs(zipBlob, fileName);
        
        // Créer une URL pour le fichier
        const fileUrl = URL.createObjectURL(zipBlob);
        console.log('ZIP file URL created:', fileUrl);
        
        // Stocker l'URL dans le localStorage pour y accéder plus tard si nécessaire
        localStorage.setItem('lastZipFileUrl', fileUrl);
        localStorage.setItem('lastZipFileName', fileName);
      } catch (error) {
        console.error('Erreur lors de la sauvegarde locale du fichier ZIP:', error);
      }
    }

    return zipFile;
  } catch (error) {
    console.error('Erreur lors de la création du fichier ZIP:', error);
    throw error;
  }
};

/**
 * Extrait l'extension d'un nom de fichier
 * @param {string} filename - Le nom du fichier
 * @returns {string} - L'extension du fichier (sans le point)
 */
function getFileExtension(filename) {
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2) || 'pdf';
}